import { useCallback, useMemo, useState } from 'react'
import { ITableField } from '../index.tsx'
import usePersistenceFields from '../usePersistenceFields.ts'
import useResizeColumnPluginCtrl from '../plugins/resize/useResizeColumnPluginCtrl.ts'

interface IProps<T extends Record<string, any>> {
  fields: ITableField<T, keyof T | string>[]
  itemKey: keyof T
  items: T[]
  tableKey: string
}

const useRegularTable = <T extends Record<string, any>>(props: IProps<T>) => {
  const [fields, setFields] = usePersistenceFields(props.fields, props.tableKey)

  const resizePluginCtrl = useResizeColumnPluginCtrl({
    columns: fields,
    tableKey: props.tableKey,
    columnsSetter: setFields,
  })
  const totalItemsCount = props.items.length

  // 0 - sort disabled, 1 - sort by asc, -1 = sort by desc
  const [orderingState, setOrderingState] = useState<Partial<Record<keyof T, -1 | 0 | 1>>>({})

  const orderedItems = useMemo(() => {
    const res = [...props.items]
    if (Object.values(orderingState).every((s) => s === undefined || s === 0)) return res

    res.sort((a, b) => {
      let result: number

      for (const field of props.fields) {
        if (orderingState[field.key] !== undefined && orderingState[field.key] !== 0) {
          result ||= comparator(
            field.sortGetter(a, field.key),
            field.sortGetter(b, field.key),
            orderingState[field.key] === 1 ? 'asc' : 'desc'
          )
        }
      }

      if (result === undefined) return 0
      return result
    })

    return res
  }, [orderingState, props.items])

  const handleSwap = useCallback(<K extends keyof T>(target: K, dropped: K) => {
    setFields((prev) => {
      const arr = [...prev]
      const targetIndex = arr.findIndex((e) => e.key === target)
      const droppedIndex = arr.findIndex((e) => e.key === dropped)
      if (targetIndex === droppedIndex) return prev

      if (targetIndex === -1 || droppedIndex === -1) return prev

      if (targetIndex > droppedIndex) {
        return [
          ...arr.slice(0, droppedIndex),
          ...arr.slice(droppedIndex + 1, targetIndex),
          arr[targetIndex],
          arr[droppedIndex],
          ...arr.slice(targetIndex + 1),
        ]
      }

      return [
        ...arr.slice(0, targetIndex),
        arr[droppedIndex],
        ...arr.slice(targetIndex).filter((it) => it.key !== dropped),
      ]
    })
  }, [])

  return {
    totalItemsCount,
    itemsPerPage: totalItemsCount,
    pageNumber: 0,

    orderedItems,
    orderingState,
    onOrderingStateChange: setOrderingState,
    onSwap: handleSwap,
    resizePluginCtrl,
    allFields: fields,
    fields,
  }
}

const comparator = (a: any, b: any, sort: 'asc' | 'desc'): number => {
  if (typeof a === 'string' && typeof b === 'string') {
    if (sort === 'desc') return b.localeCompare(a)
    return a.localeCompare(b)
  }

  if (typeof a === 'number' && typeof b === 'number') {
    if (sort === 'desc') return b - a
    return a - b
  }

  return 0
}

export default useRegularTable
